import React, { useEffect, useState, useMemo } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { auditApi } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";

const AuthorizationList = ({ onEdit, searchConsumer }) => {
  const [auths, setAuths] = useState([]);
  const [loading, setLoading] = useState(true);
  const { tenant_id, user, loadingUser } = useAuth();

  // Filter states
  const [filters, setFilters] = useState({
    authNumber: "",
    consumer: searchConsumer || "",
    authType: "",
    status: "",
    service: "",
  });

  // Update consumer filter when prop changes
  useEffect(() => {
    setFilters((prev) => ({ ...prev, consumer: searchConsumer || "" }));
  }, [searchConsumer]);

  useEffect(() => {
    if (loadingUser || !tenant_id) return;

    const fetchAuthorizations = async () => {
      try {
        setLoading(true);
        const res = await secureAxios.get("/api/auths");
        console.log("Authorizations loaded:", res.data);
        setAuths(res.data);

        await auditApi.logEntry({
          action: "VIEW_AUTHORIZATION_LIST",
          entity_type: "AUTHORIZATION",
          action_status: "SUCCESS",
          purpose_of_use: "OPERATIONS",
          contains_phi: false,
          details: { record_count: res.data.length },
        });
      } catch (err) {
        console.error("Failed to load authorizations", err);
        toast.error("Failed to load authorizations");

        await auditApi.logEntry({
          action: "VIEW_AUTHORIZATION_LIST",
          entity_type: "AUTHORIZATION",
          action_status: "FAILED",
          action_reason: err.message,
          purpose_of_use: "OPERATIONS",
          contains_phi: false,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAuthorizations();
  }, [tenant_id, loadingUser]);

  const handleDelete = async (auth) => {
    const confirmMessage =
      "Delete this authorization?\n\n" +
      "WARNING: This action cannot be undone.\n" +
      "This deletion will be permanently logged for HIPAA compliance.\n\n" +
      "Authorization #" +
      (auth.authorization_number || auth.authorization_id) +
      "\n" +
      "Consumer: " +
      auth.consumer_name;

    if (!window.confirm(confirmMessage)) return;

    try {
      await secureAxios.delete(`/api/auths/${auth.authorization_id}`);
      setAuths((prev) =>
        prev.filter((a) => a.authorization_id !== auth.authorization_id)
      );
      toast.success("Authorization deleted successfully");

      await auditApi.logEntry({
        action: "DELETE_AUTHORIZATION",
        entity_type: "AUTHORIZATION",
        entity_id: auth.authorization_id,
        entity_identifier: `Auth #${auth.authorization_number} for ${auth.consumer_name}`,
        action_status: "SUCCESS",
        purpose_of_use: "OPERATIONS",
        contains_phi: false,
      });
    } catch (err) {
      console.error("Delete failed", err);
      toast.error("Failed to delete authorization");

      await auditApi.logEntry({
        action: "DELETE_AUTHORIZATION",
        entity_type: "AUTHORIZATION",
        entity_id: auth.authorization_id,
        action_status: "FAILED",
        action_reason: err.message,
        purpose_of_use: "OPERATIONS",
        contains_phi: false,
      });
    }
  };

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    console.log("Filter change:", name, "=", value);
    setFilters((prev) => {
      const newFilters = { ...prev, [name]: value };
      console.log("New filters state:", JSON.stringify(newFilters));
      return newFilters;
    });
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      authNumber: "",
      consumer: "",
      authType: "",
      status: "",
      service: "",
    });
  };

  // Apply all filters with debug logging
  const filteredAuths = useMemo(() => {
    console.log("AuthorizationList - Applying filters:", JSON.stringify(filters));
    console.log("AuthorizationList - Total auths:", auths.length);
    
    const result = auths.filter((a) => {
      // Authorization number filter
      if (filters.authNumber) {
        const authNum = String(a.authorization_number || "").toLowerCase();
        if (!authNum.includes(filters.authNumber.toLowerCase())) {
          return false;
        }
      }

      // Consumer name filter
      if (filters.consumer) {
        const consumerName = (a.consumer_name || "").toLowerCase();
        const searchTerm = filters.consumer.toLowerCase().trim();
        if (!consumerName.includes(searchTerm)) {
          return false;
        }
      }

      // Auth type filter
      if (filters.authType && a.auth_type !== filters.authType) {
        return false;
      }

      // Status filter
      if (filters.status === "complete" && !a.complete) {
        return false;
      }
      if (filters.status === "incomplete" && a.complete) {
        return false;
      }

      // Service filter
      if (filters.service) {
        const serviceName = (a.service_name || "").toLowerCase();
        if (!serviceName.includes(filters.service.toLowerCase())) {
          return false;
        }
      }

      return true;
    });
    
    console.log("AuthorizationList - Filtered results:", result.length);
    return result;
  }, [auths, filters]);

  // Get unique values for dropdowns
  const uniqueAuthTypes = [
    ...new Set(auths.map((a) => a.auth_type).filter(Boolean)),
  ];
  const uniqueServices = [
    ...new Set(auths.map((a) => a.service_name).filter(Boolean)),
  ];

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  const activeFilterCount = Object.values(filters).filter(
    (v) => v !== ""
  ).length;

  return (
    <>
      {/* Filter Section */}
      <div className="card mb-3">
        <div className="card-header bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">
              <i className="bi bi-funnel me-2"></i>
              Filters
              {activeFilterCount > 0 && (
                <span className="badge bg-primary ms-2">
                  {activeFilterCount}
                </span>
              )}
            </h6>
            {activeFilterCount > 0 && (
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={clearFilters}
              >
                <i className="bi bi-x-circle me-1"></i>
                Clear All
              </button>
            )}
          </div>
        </div>
        <div className="card-body">
          <div className="row g-3">
            {/* Auth Number Search */}
            <div className="col-md-2">
              <label className="form-label small">Auth#</label>
              <input
                type="text"
                className="form-control form-control-sm"
                name="authNumber"
                placeholder="Search auth#..."
                value={filters.authNumber}
                onChange={handleFilterChange}
              />
            </div>

            {/* Consumer Search */}
            <div className="col-md-3">
              <label className="form-label small">Consumer Name</label>
              <input
                type="text"
                className="form-control form-control-sm"
                name="consumer"
                placeholder="Search consumer..."
                value={filters.consumer}
                onChange={handleFilterChange}
              />
            </div>

            {/* Auth Type Filter */}
            <div className="col-md-2">
              <label className="form-label small">Authorization Type</label>
              <select
                className="form-select form-select-sm"
                name="authType"
                value={filters.authType}
                onChange={handleFilterChange}
              >
                <option value="">All Types</option>
                {uniqueAuthTypes.map((type) => (
                  <option key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div className="col-md-2">
              <label className="form-label small">Status</label>
              <select
                className="form-select form-select-sm"
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="">All Statuses</option>
                <option value="complete">Complete</option>
                <option value="incomplete">Incomplete</option>
              </select>
            </div>

            {/* Service Filter */}
            <div className="col-md-3">
              <label className="form-label small">Service</label>
              <select
                className="form-select form-select-sm"
                name="service"
                value={filters.service}
                onChange={handleFilterChange}
              >
                <option value="">All Services</option>
                {uniqueServices.sort().map((service) => (
                  <option key={service} value={service}>
                    {service}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Results Count */}
          <div className="mt-3 pt-3 border-top">
            <small className="text-muted">
              Showing <strong>{filteredAuths.length}</strong> of{" "}
              <strong>{auths.length}</strong> authorizations
            </small>
          </div>
        </div>
      </div>

      {/* Desktop/tablet table view */}
      <div className="table-responsive d-none d-md-block">
        <table className="table table-sm table-hover">
          <thead className="table-light">
            <tr>
              <th>Auth#</th>
              <th>Consumer</th>
              <th>Service</th>
              <th>Hours</th>
              <th>Approved</th>
              <th>Type</th>
              <th>Complete</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAuths.map((a) => (
              <tr key={a.authorization_id}>
                <td>{a.authorization_number}</td>
                <td>
                  <div>{a.consumer_name}</div>
                  <small className="text-muted">
                    <i className="bi bi-person-badge me-1"></i>
                    {a.job_coach_name || (
                      <span className="fst-italic">No coach assigned</span>
                    )}
                  </small>
                </td>
                <td>
                  <div>
                    {a.service_name}
                    {a.flat_rate && (
                      <span 
                        className={`badge ms-1 ${
                          parseInt(a.milestone_total) > 0 && parseInt(a.milestone_invoiced) === parseInt(a.milestone_total)
                            ? "bg-success"
                            : parseInt(a.milestone_invoiced) > 0
                            ? "bg-info"
                            : "bg-warning text-dark"
                        }`}
                        title={
                          parseInt(a.milestone_total) > 0
                            ? `${a.milestone_invoiced} of ${a.milestone_total} milestones invoiced`
                            : "Flat Rate - No milestones defined"
                        }
                        style={{ fontSize: '0.65em' }}
                      >
                        {parseInt(a.milestone_total) > 0 ? (
                          <>
                            Flat Rate ({a.milestone_invoiced}/{a.milestone_total})
                            {parseInt(a.milestone_invoiced) === parseInt(a.milestone_total) && parseInt(a.milestone_total) > 0 && (
                              <i className="bi bi-check-circle ms-1"></i>
                            )}
                          </>
                        ) : (
                          "Flat Rate"
                        )}
                      </span>
                    )}
                  </div>
                </td>
                <td>{a.authorization_hours || "-"}</td>
                <td>{a.approved_by ? "Yes" : "-"}</td>
                <td>
                  <span
                    className={`badge ${
                      a.auth_type === "service" ? "bg-primary" : "bg-info"
                    }`}
                  >
                    {a.auth_type || "-"}
                  </span>
                </td>
                <td>
                  {a.complete ? (
                    <span className="badge bg-success">Complete</span>
                  ) : (
                    <span className="badge bg-warning text-dark">
                      Pending
                    </span>
                  )}
                </td>
                <td>
                  <button
                    className="btn btn-sm btn-primary me-2"
                    onClick={() => onEdit(a)}
                  >
                    <i className="bi bi-pencil"></i>
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleDelete(a)}
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile list view */}
      <ul className="list-group d-block d-md-none">
        {filteredAuths.map((a) => (
          <li key={a.authorization_id} className="list-group-item">
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div>
                <strong>Auth#:</strong> {a.authorization_number}
              </div>
              {a.complete ? (
                <span className="badge bg-success">Complete</span>
              ) : (
                <span className="badge bg-warning text-dark">Pending</span>
              )}
            </div>
            <div>
              <strong>Consumer:</strong> {a.consumer_name}
              <br />
              <small className="text-muted">
                <i className="bi bi-person-badge me-1"></i>
                {a.job_coach_name || (
                  <span className="fst-italic">No coach assigned</span>
                )}
              </small>
            </div>
            <div>
              <strong>Service:</strong> {a.service_name}
              {a.flat_rate && (
                <span 
                  className={`badge ms-1 ${
                    parseInt(a.milestone_total) > 0 && parseInt(a.milestone_invoiced) === parseInt(a.milestone_total)
                      ? "bg-success"
                      : parseInt(a.milestone_invoiced) > 0
                      ? "bg-info"
                      : "bg-warning text-dark"
                  }`}
                  title={
                    parseInt(a.milestone_total) > 0
                      ? `${a.milestone_invoiced} of ${a.milestone_total} milestones invoiced`
                      : "Flat Rate - No milestones defined"
                  }
                  style={{ fontSize: '0.65em' }}
                >
                  {parseInt(a.milestone_total) > 0 ? (
                    <>
                      Flat Rate ({a.milestone_invoiced}/{a.milestone_total})
                      {parseInt(a.milestone_invoiced) === parseInt(a.milestone_total) && parseInt(a.milestone_total) > 0 && (
                        <i className="bi bi-check-circle ms-1"></i>
                      )}
                    </>
                  ) : (
                    "Flat Rate"
                  )}
                </span>
              )}
            </div>
            <div>
              <strong>Hours:</strong> {a.authorization_hours || "-"}
            </div>
            <div>
              <strong>Approved:</strong> {a.approved_by ? "Yes" : "-"}
            </div>
            <div>
              <strong>Type:</strong>{" "}
              <span
                className={`badge ${
                  a.auth_type === "service" ? "bg-primary" : "bg-info"
                }`}
              >
                {a.auth_type || "-"}
              </span>
            </div>
            <div className="mt-2 d-flex gap-2 flex-wrap">
              <button
                className="btn btn-sm btn-primary"
                onClick={() => onEdit(a)}
              >
                <i className="bi bi-pencil me-1"></i>Edit
              </button>
              <button
                className="btn btn-sm btn-danger"
                onClick={() => handleDelete(a)}
              >
                <i className="bi bi-trash me-1"></i>Delete
              </button>
            </div>
          </li>
        ))}
      </ul>

      {filteredAuths.length === 0 && (
        <div className="alert alert-info text-center">
          <i className="bi bi-info-circle me-2"></i>
          {activeFilterCount > 0
            ? "No authorizations found matching your filters."
            : "No authorizations found."}
        </div>
      )}
    </>
  );
};

export default AuthorizationList;